import { AppPageHeader, AppSectionErrorBoundary, AppUseIcon, AppUserTypeChip } from 'components/ui';
import { License, UserType } from 'types/ProfileTypes';
import { SingleInviteType, SingleUserType, UserTypes } from 'types/GlobalTypes';
import { useEffect, useMemo, useState } from 'react';

import { AppButton } from 'components/form';
import { AppPermissions } from 'utilities/Constants';
import { AppTableFilters } from 'services/AppTableFilters';
import { Authorization } from 'components/Authorization';
import { OrganizationInvite } from 'types/models/OrganizationInvite';
import { SpriteStateIconsEnum } from 'types/Enums';
import { StaticTableWithFilters } from 'components/Tables/StaticTableWithFilters';
import { TableCell } from '@trackman/web-shared-components';
import UserDialog from './UserDialog';
import { getColor } from 'utilities/SystemUtils';
import { observer } from 'mobx-react-lite';
import { t } from 'utilities/TranslationUtils';
import { useStores } from 'index';

const appTableFiltersInstance = new AppTableFilters();

const UsersPage: React.FunctionComponent = observer(() => {
  const { usersStore, teamPortalApiAxiosInstance, inviteStore, profileStore } = useStores();
  const { users, isLoading: isLoadingUsers } = usersStore;
  const { invites, isLoading: isLoadingInvites } = inviteStore;

  const [isUserDialogOpened, setIsUserDialogOpened] = useState(false);

  const [selectedUser, setSelectedUser] = useState<(SingleUserType & OrganizationInvite) | null>(null);

  const isUserActive = (user: SingleInviteType | OrganizationInvite) => user && user?.status === 'active';
  // in the method below i am using ".includes('pending')" because the pending is sent form the backend like "PendingUserAction"...
  const isUserPending = (user: SingleInviteType | OrganizationInvite) => user && user?.status?.toLowerCase()?.includes('pending');

  const rows = useMemo(() => {
    // I used "".filter(isUserPending)" to remove abandoned and finalized invites as requested from the team.
    // this solution should be temporary until the invites get moved to an invitation system that is under development.
    return [
      ...(invites.map((invite) => ({ ...invite, status: invite['status'] || 'pending' })).filter(isUserPending) || []),
      ...(users.map((user) => ({ ...user, status: 'active' })) || []),
    ];
  }, [users, invites]);

  const columns = useMemo(
    () => [
      {
        id: 'name',
        Header: t('common.name'),
        maxWidth: 100,
        filter: {
          id: 'name',
          type: 'textfields',
          onApply: ({ firstName, lastName }: { firstName: string; lastName: string }) => {
            if (firstName) {
              appTableFiltersInstance.addFilter({
                id: 'firstName',
                type: 'textfields',
                label: `${t('common.firstName')}: ${firstName}`,
                field: 'firstName',
                value: firstName,
              });
            }
            if (lastName) {
              appTableFiltersInstance.addFilter({
                id: 'lastName',
                type: 'textfields',
                label: `${t('common.lastName')}: ${lastName}`,
                field: 'lastName',
                value: lastName,
              });
            }
          },
          inputs: [
            {
              id: 'firstName',
              // this is the field inside the data object that should be used to filter the data
              field: 'firstName',
              label: t('common.firstName'),
              value: '',
            },
            {
              id: 'lastName',
              // this is the field inside the data object that should be used to filter the data
              field: 'lastName',
              label: t('common.lastName'),
              value: '',
            },
          ],
        },
      },
      {
        id: 'email',
        Header: t('common.email'),
        accessor: 'email',
        maxWidth: 120,
        filter: {
          id: 'email',
          type: 'textfields',
          onApply: ({ email }: { email: string }) => {
            if (email) {
              appTableFiltersInstance.addFilter({
                id: 'email',
                type: 'textfields',
                label: `${t('common.email')}: ${email}`,
                field: 'email',
                value: email,
              });
            }
          },
          inputs: [
            {
              id: 'email',
              // this is the field inside the data object that should be used to filter the data
              field: 'email',
              label: t('common.email'),
              value: '',
            },
          ],
        },
      },
      {
        id: 'userType',
        Header: t('common.userType'),
        accessor: 'type',
        maxWidth: 90,
        filter: {
          id: 'userType',
          type: 'select',
          field: 'type',
          value: '',
          label: t('common.userType'),
          options: [
            { label: `${t('common.admin')}`, value: UserType.Admin },
            { label: `${t('common.user')}`, value: UserType.User },
            { label: `${t('common.coach')}`, value: UserType.Coach },
          ],
          onApply: (userType) => {
            if (userType) {
              appTableFiltersInstance.addFilter({
                id: 'userType',
                type: 'select',
                label: `${t('common.userType')}: ${userType}`,
                field: 'type',
                value: userType,
                options: [
                  { label: `${t('common.admin')}`, value: UserType.Admin },
                  { label: `${t('common.user')}`, value: UserType.User },
                  { label: `${t('common.coach')}`, value: UserType.Coach },
                ],
              });
            }
          },
        },
      },
      {
        id: 'status',
        Header: t('common.status'),
        accessor: 'status',
        maxWidth: 100,
        filter: {
          id: 'status',
          type: 'radio',
          field: 'status',
          label: t('common.status'),
          value: '',
          options: [
            { value: 'active', label: t('common.active') },
            { value: 'pending', label: t('common.pending') },
          ],
          onApply: (status) => {
            if (status) {
              appTableFiltersInstance.addFilter({
                id: 'status',
                type: 'radio',
                label: `${t('common.status')}: ${t(`common.${status}`)}`,
                field: 'status',
                value: status,
                options: [
                  { value: 'active', label: t('common.active') },
                  { value: 'pending', label: t('common.pending') },
                ],
                filteringFn(item, value) {
                  return item.status?.toLowerCase().includes(value);
                },
              });
            }
          },
        },
      },
    ],
    []
  );

  useEffect(() => {
    handleRefreshingData();
  }, []);

  const handleRefreshingData = async () => {
    await usersStore.LoadData(teamPortalApiAxiosInstance);
    await inviteStore.LoadData(teamPortalApiAxiosInstance);
  };
  const renderCell = ({
    cellValue,
    columnHeader,
    columnId,
    row,
  }: {
    cellValue: string;
    columnHeader: string;
    columnId: string;
    row: any;
  }) => {
    if (columnId === 'name') {
      return (
        <span className='overflow-ellipsis' title={`${row.original.firstName} ${row.original.lastName}`}>
          {row?.original?.firstName ? `${row.original.firstName} ${row.original.lastName}` : cellValue?.trim() || '-'}
        </span>
      );
    }

    if (columnId === 'userType') {
      return <AppUserTypeChip userType={cellValue as UserTypes} />;
    }

    if (columnId === 'status') {
      return (
        <span className='d-flex align-items-center'>
          <AppUseIcon name={`${SpriteStateIconsEnum[cellValue?.toLowerCase()]}`} height='14' width='14' />
          <span className={`ml-2 text-capitalize text-${getColor(cellValue as UserTypes)}`}>
            {cellValue ? t(`common.${cellValue}`) : ''}
          </span>
        </span>
      );
    }

    return (
      <span className='overflow-ellipsis ' title={`${cellValue?.trim() || '-'}`}>
        {cellValue?.trim() || '-'}
      </span>
    );
  };

  const rowMapper = (cells: any[]) => {
    return (
      <>
        {cells.map(({ getCellProps, column, row, render, value }) => (
          <TableCell
            {...getCellProps()}
            align={column.align}
            onClick={() => {
              const player = JSON.parse(JSON.stringify(row.original));
                setSelectedUser(player);
                setIsUserDialogOpened(true);
            }}
          >
            {renderCell({ cellValue: value, columnHeader: column.Header, columnId: column.id, row })}
          </TableCell>
        ))}
      </>
    );
  };

  // ============================================================

  // submit : -send invite || -resend invite || -update user || -create user
  const submitUserHandler = async (
    data: SingleInviteType & OrganizationInvite,
    onSuccessFn: (e) => void = (e) => {},
    onErrorFn: (e) => void = (e) => {}
  ) => {
    if (!data) return;

    if (!isUserActive(data) && !isUserPending(data)) handleSendingInvitation({ ...data, playerId: '' }, onSuccessFn, onErrorFn);

    if (isUserPending(data)) handleResendingInvitation(data, onSuccessFn, onErrorFn);
  };

  const handleSendingInvitation = (data: SingleInviteType & OrganizationInvite, onSuccessFn: (e) => void, onErrorFn: (e) => void) => {
    inviteStore.SendInvite(
      teamPortalApiAxiosInstance,
      {
        email: data.email,
        type: 'Coach',
        ...(selectedUser?.playerId ? { playerId: selectedUser.playerId } : {}),
      },
      () => onSuccessFn({}),
      () => onErrorFn({})
    );
  };

  const handleResendingInvitation = (data: SingleInviteType & OrganizationInvite, onSuccessFn: (e) => void, onErrorFn: (e) => void) => {
    inviteStore.ResendInvite(
      teamPortalApiAxiosInstance,
      data.id,
      () => onSuccessFn({}),
      () => onErrorFn({})
    );
  };

  return (
    <AppSectionErrorBoundary>
      <div className='users-tables'>
        <AppPageHeader title={t('management.users')}>
          <>
            <AppButton
              className='position-absolute right-0 mt-auto mb-auto'
              variant='transparent'
              disabled={isLoadingUsers}
              data-test='add-user-player-button'
              id='add-user-button'
              onClick={() => setIsUserDialogOpened(true)}
            >
              {t('management.addUser')}
              <AppUseIcon name='PlusIconWithoutBorder' width='14' height='14' className='ml-2' />
            </AppButton>
          </>
        </AppPageHeader>

        <StaticTableWithFilters<SingleInviteType>
          appTableFiltersInstance={appTableFiltersInstance}
          rowMapper={rowMapper}
          columns={columns}
          data={rows as SingleInviteType[]}
          isLoading={isLoadingUsers || isLoadingInvites}
        />

        <UserDialog
          isLoading={isLoadingUsers || isLoadingInvites || inviteStore.isCancelingInvite || usersStore.isRemovingUser}
          selectedUser={selectedUser}
          onClose={() => {
            setIsUserDialogOpened(false);
            setTimeout(() => setSelectedUser(null), 300);
          }}
          onSubmit={(data: any, onSuccessFn, onErrorFn) => submitUserHandler(data, onSuccessFn, onErrorFn)}
          isDialogOpened={isUserDialogOpened}
          onRemoveUser={() => {
            setIsUserDialogOpened(false);
            setSelectedUser(null);
            handleRefreshingData();
          }}
        />
      </div>
    </AppSectionErrorBoundary>
  );
});

export default Authorization(UsersPage, AppPermissions.ManageUsers);
